import moment from 'moment';

import {
  getRangePickerValue,
  TYPE_TO_GRANULARITY,
} from '@intelligence/v3/filterControls/rangePicker/utils';

import type { IFilterValues } from '@intelligence/v3/filterCore/types';

export interface IRobloxResolvedDateRange {
  startDate: string;
  endDate: string;
  granularity?: IGranularity;
}

export interface IRobloxApiRequestMeta {
  start_date?: string;
  end_date?: string;
  granularity?: string;
  date_type?: string;
}

export const hasRobloxFilterDate = (filterValues?: IFilterValues): boolean => {
  const { startDate, endDate } = getRangePickerValue(filterValues?.date);

  return Boolean(startDate && endDate);
};

export const parseRobloxApiRequestMeta = (response?: any): IRobloxApiRequestMeta => {
  const req = response?.req ?? {};

  return {
    start_date: req.start_date || '',
    end_date: req.end_date || '',
    granularity: req.granularity || '',
    date_type: req.date_type || '',
  };
};

const getGranularityFromReq = (req: IRobloxApiRequestMeta): IGranularity => {
  const rawGranularity = req.granularity || req.date_type || '';

  if (TYPE_TO_GRANULARITY[rawGranularity]) {
    return TYPE_TO_GRANULARITY[rawGranularity];
  }

  if (rawGranularity === 'daily' || rawGranularity === 'weekly' || rawGranularity === 'monthly') {
    return rawGranularity;
  }

  return 'daily';
};

export const getRobloxResolvedDateRangeFromReq = (
  req: IRobloxApiRequestMeta,
): IRobloxResolvedDateRange | null => {
  if (!req?.start_date || !req?.end_date) {
    return null;
  }

  return {
    startDate: req.start_date,
    endDate: req.end_date,
    granularity: getGranularityFromReq(req),
  };
};

export const mergeRobloxFilterValuesWithResolvedDateRange = (
  filterValues: IFilterValues,
  resolvedDateRange: IRobloxResolvedDateRange | null,
): IFilterValues | null => {
  if (!resolvedDateRange || hasRobloxFilterDate(filterValues)) {
    return null;
  }

  const currentDate = getRangePickerValue(filterValues?.date);

  return {
    ...filterValues,
    date: {
      granularity: resolvedDateRange.granularity || currentDate.granularity || 'daily',
      startDate: resolvedDateRange.startDate,
      endDate: resolvedDateRange.endDate,
    },
  };
};

export const formatRobloxApiDateByGranularity = (
  date: string,
  granularity?: IGranularity,
): string => {
  if (!date) {
    return date;
  }

  if (granularity === 'monthly') {
    return moment(date).startOf('month').format('YYYY-MM-DD');
  }

  return date;
};

export const getRobloxFetchDateRange = (
  filterValues?: IFilterValues,
): { start_date?: string; end_date?: string } => {
  const { startDate, endDate, granularity } = getRangePickerValue(filterValues?.date);

  if (!startDate && !endDate) {
    return {};
  }

  const rawStartDate = startDate || endDate;
  const rawEndDate = endDate || startDate;

  return {
    start_date: formatRobloxApiDateByGranularity(rawStartDate, granularity),
    end_date: formatRobloxApiDateByGranularity(rawEndDate, granularity),
  };
};

export const appendRobloxFetchDateParams = <T extends Record<string, any>>(
  params: T,
  filterValues?: IFilterValues,
): T => {
  const dateParams = getRobloxFetchDateRange(filterValues);

  if (!dateParams.start_date && !dateParams.end_date) {
    return params;
  }

  return {
    ...params,
    ...dateParams,
  };
};
